import { Controller } from "@hotwired/stimulus"
import { visit } from "@hotwired/turbo"
import { signInResponse } from "./multi_target_revalidation_stream"

// UI state only: Rails owns ordering, cursors, filters and all money semantics.
export function activityRequestState(frame) {
  const card = frame?.querySelector("[data-controller~='machine-activity']")
  if (!card) return null
  return JSON.stringify({ machine_id: card.dataset.activityMachineId,
    filter: card.dataset.activityFilter, through: card.dataset.activityThrough,
    revision: card.dataset.activityRevision })
}

export default class extends Controller {
  connect() {
    this.generation = 0
    this.groupDates()
  }

  disconnect() {
    this.cancel()
  }

  cancel() {
    this.generation += 1
    this.request?.abort()
    this.request = null
    if (this.loadingLink?.isConnected && this.loadingLink.textContent === "Loading…") {
      this.loadingLink.textContent = this.loadingLabel
    }
    this.loadingLink = null
    this.element.removeAttribute("data-refresh-hold")
    this.element.removeAttribute("aria-busy")
  }

  revise() {
    this.element.dataset.activityRevision = String(Number(this.element.dataset.activityRevision || 0) + 1)
  }

  beforeFetch(event) {
    const frame = this.element.closest("turbo-frame[data-src]")
    if (!frame?.contains(event.target) || this.element.contains(event.target)) return
    this.cancel()
    this.revise()
    event.detail.fetchOptions.headers["X-Machine-Activity"] = activityRequestState(frame)
  }

  async navigate(event) {
    const link = event.target.closest("a[data-activity-more], a[data-activity-filter-link]")
    if (!link || !this.element.contains(link)) return
    event.preventDefault()
    event.stopPropagation()
    const root = this.element.closest("#machine-root")
    const revalidation = this.application.getControllerForElementAndIdentifier(root, "machine-revalidation")
    if (revalidation?.submissionHeld || this.element.closest("[inert]")) return
    revalidation?.invalidate()
    this.cancel()
    this.revise()
    const generation = this.generation
    const append = link.hasAttribute("data-activity-more")
    const expectedFilter = append ? this.element.dataset.activityFilter : link.dataset.activityFilterLink
    const request = new AbortController()
    this.request = request
    this.element.setAttribute("data-refresh-hold", "")
    this.element.setAttribute("aria-busy", "true")
    const oldLabel = link.textContent
    this.loadingLink = link
    this.loadingLabel = oldLabel
    link.textContent = "Loading…"
    const timeout = window.setTimeout(() => request.abort(), 10_000)
    try {
      const response = await fetch(link.href, { credentials: "same-origin", signal: request.signal,
        headers: { Accept: "text/html", "Turbo-Frame": this.element.id } })
      if (generation !== this.generation || !this.element.isConnected) return
      if (signInResponse(response)) { visit(response.url, { action: "replace" }); return }
      if (!response.ok || !response.headers.get("Content-Type")?.includes("text/html")) throw new Error("Activity request failed")
      const document = new DOMParser().parseFromString(await response.text(), "text/html")
      if (generation !== this.generation || !this.element.isConnected) return
      const incoming = document.getElementById(this.element.id)
      if (!incoming || incoming.dataset.activityFilter !== expectedFilter) throw new Error("Wrong activity response")
      const anchor = this.anchor()
      if (append) {
        const list = this.element.querySelector(".mh-list")
        incoming.querySelectorAll("[data-activity-row]").forEach((row) => {
          if (!this.element.querySelector(`#${CSS.escape(row.id)}`)) list.append(row)
        })
        this.element.querySelector("[data-activity-footer]").replaceWith(incoming.querySelector("[data-activity-footer]"))
      } else {
        this.element.replaceChildren(...incoming.childNodes)
      }
      this.element.dataset.activityFilter = incoming.dataset.activityFilter
      this.element.dataset.activityThrough = incoming.dataset.activityThrough || (append ? this.element.dataset.activityThrough : "") || ""
      this.revise()
      this.groupDates()
      this.restoreAnchor(anchor)
    } catch (error) {
      if (generation !== this.generation || !this.element.isConnected) return
      const footer = this.element.querySelector("[data-activity-footer]")
      footer.hidden = false
      footer.querySelector("[data-activity-error]").hidden = false
      // Retry exactly the failed user intent, including a hidden-filter request.
      const retry = footer.querySelector("a")
      retry.href = link.href
      if (append) { retry.setAttribute("data-activity-more", ""); delete retry.dataset.activityFilterLink }
      else { retry.dataset.activityFilterLink = expectedFilter; retry.removeAttribute("data-activity-more") }
      retry.textContent = "Try again"
    } finally {
      window.clearTimeout(timeout)
      if (generation === this.generation) {
        this.request = null
        this.loadingLink = null
        this.element.removeAttribute("data-refresh-hold")
        this.element.removeAttribute("aria-busy")
        if (link.isConnected && link.textContent === "Loading…") link.textContent = oldLabel
      }
    }
  }

  beforeStream(event) {
    const stream = event.target
    if (stream.getAttribute("target") !== this.element.id) return
    const incoming = stream.querySelector("template")?.content.getElementById(this.element.id)
    if (this.request || !incoming || incoming.dataset.activityRevision !== this.element.dataset.activityRevision ||
        incoming.dataset.activityFilter !== this.element.dataset.activityFilter) {
      event.preventDefault()
      return
    }
    const render = event.detail.render
    event.detail.render = async (element) => {
      const anchor = this.anchor()
      await render(element)
      this.groupDates()
      this.restoreAnchor(anchor)
    }
  }

  anchor() {
    if (this.element.closest("[inert]")) return null
    const row = Array.from(this.element.querySelectorAll("[data-activity-row]")).find((item) => item.getBoundingClientRect().bottom > 70)
    if (!row || row.getBoundingClientRect().top > window.innerHeight) return null
    return { id: row.id, top: row.getBoundingClientRect().top }
  }

  restoreAnchor(anchor) {
    if (!anchor) return
    const row = document.getElementById(anchor.id)
    if (row) window.scrollBy(0, row.getBoundingClientRect().top - anchor.top)
  }

  groupDates() {
    let date
    this.element.querySelectorAll("[data-activity-row]").forEach((row) => {
      row.querySelector(".mh-date").hidden = row.dataset.occurredDate === date
      date = row.dataset.occurredDate
    })
  }
}
